import { Icon, Slider } from "@blueprintjs/core";
import classNames from "classnames";
import queryString from "query-string";
import React, { useEffect, useState } from "react";
import { useReactToPrint } from "react-to-print";

import { SportPlay } from "../../types";
import { Button } from "../Button/button";
import { PlayVideoDisplay } from "../play-video-display/play-video-display";
import { ReadOnlyPlayComponent } from "../read-only-play/read-only-play";
import { Tag } from "../tag/tag";
import { PlayFramesDisplay } from "./play-frames-display";
import styles from "./viewboard.module.css";

export interface ViewboardProps {
  activePlay: SportPlay;
  handleClickEditPlay: () => void;
  printableRef: React.RefObject<HTMLDivElement | null>;
}

const Viewboard = ({ activePlay, handleClickEditPlay, printableRef }: ViewboardProps) => {
  const { season, team, series, name } = activePlay;

  const [viewMode, setViewMode] = useState<"frame" | "animation" | "attachments">("frame");
  const [frameZoom, setFrameZoom] = useState<number>(10);

  const handlePrint = useReactToPrint({
    content: () => printableRef.current,
    bodyClass: styles.printBody,
  });

  let content = <></>;
  if (viewMode === "frame") {
    content = <PlayFramesDisplay activePlay={activePlay} frameZoom={frameZoom} />;
  } else if (viewMode === "animation") {
    content = (
      <div className={styles.playContainer}>
        <div className={styles.playAnimation}>
          <ReadOnlyPlayComponent play={activePlay} frameIndex={undefined} interactive={true} />
        </div>
      </div>
    );
  } else if (viewMode === "attachments") {
    content = <PlayVideoDisplay play={activePlay} />;
  }

  // if modal=synergy is in url switch to attachments view
  useEffect(() => {
    const hashParams = queryString.parse(window.location.hash);
    const modal = hashParams.modal;

    if (modal === "synergy") {
      setViewMode("attachments");
    }
  }, []);

  return (
    <>
      <h5 className={styles.header}>
        <div className={styles.upperHeaderContainer}>
          <div className={styles.playLabels}>{`${season} ${team?.name ? `• ${team.name}` : ""}  ${
            series?.name ? `• ${series?.name}` : ""
          } • ${name}`}</div>

          <div className={styles.editFlexItem}>
            <Button id="editPlayButton" onClick={handleClickEditPlay} className={styles.hideOnPrint}>
              Edit Play
            </Button>
          </div>
        </div>
        {activePlay.tags.length > 0 && (
          <div className={styles.tagContainer}>
            {activePlay.tags.map((item, index) => (
              <Tag key={index} tag={item} />
            ))}
          </div>
        )}
      </h5>
      <div className={styles.subHeaderContainer}>
        <div className={styles.frameViewIconContainer}>
          <Icon
            icon={"media"}
            className={classNames(styles.icon, [viewMode === "frame" ? styles.selected : "", styles.hideOnPrint])}
            size={20}
            onClick={() => setViewMode("frame")}
          />
          <Icon
            icon={"video"}
            className={classNames(styles.icon, [viewMode === "animation" ? styles.selected : "", styles.hideOnPrint])}
            size={20}
            onClick={() => setViewMode("animation")}
          />
          <Icon
            icon={"paperclip"}
            className={classNames(styles.icon, [viewMode === "attachments" ? styles.selected : "", styles.hideOnPrint])}
            size={20}
            onClick={() => setViewMode("attachments")}
          />
        </div>
        {viewMode === "frame" && (
          <div className={classNames(styles.zoomContainer, [styles.hideOnPrint])}>
            <Slider min={3} max={20} value={frameZoom} onChange={(val) => setFrameZoom(val)} labelRenderer={false} />
            <Icon icon={"zoom-to-fit"} className={styles.icon} style={{ cursor: "default" }} />
            <Icon
              icon={"print"}
              className={classNames(styles.icon, [styles.hideOnPrint])}
              size={20}
              onClick={handlePrint}
            />
          </div>
        )}
      </div>
      {content}
    </>
  );
};

export default Viewboard;
